import SourceFirstEditor from "./SourceFirstEditor";
import { useContext } from "react";
import { OutsourceOrder } from "../../../data/entities";
import { OUTSOURCE_ORDER_STATUS, ROLE } from "../../../data/enums";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import { api } from "../../../shared/hooks/api";
import apiMethods from "../../../utils/api";
import ProviderInfo from "../../MaterialOrder/single/ProviderInfo";
import Note from "../../ProductOrder/single/Note";
import Priority from "../../ProductOrder/single/Priority";
import { SecondColumn } from "../../ProductOrder/single/Styled";
import {
    OutsourceWorkflowShell,
} from "../components/primitives";
import { OutsourceDetailColumns } from "./Styled";
import { normalizeOutsourceMaterials } from "../utils";
import { Assignees } from "./Assignees";
import { MaterialList } from "./MaterialList";
import { SourceMaterialList } from "./SourceMaterialList";
import Summary from "./Summary";

interface SingleOutsourceOrderProps {
    outsourceOrder: OutsourceOrder;
    mergeOutsourceOrder: Function;
    fetchOutsourceOrder?: any;
    isView?: boolean;
}

export const SingleOutsourceOrder = ({
    outsourceOrder,
    mergeOutsourceOrder,
    fetchOutsourceOrder,
    isView = false,
}: SingleOutsourceOrderProps) => {
    const materials = normalizeOutsourceMaterials(outsourceOrder?.materials as any);
    const shouldFetchProvider = Boolean((outsourceOrder as any)?.provider_id && !(outsourceOrder as any)?.provider);
    const [{ data: fetchedProvider }] = api.get(
        `/materials/providers/${(outsourceOrder as any)?.provider_id}/`,
        undefined,
        !shouldFetchProvider,
    );
    const orderProvider = (outsourceOrder as any)?.provider || fetchedProvider || null;

    const updateOutsourceOrder = async (data: any) => {
        await apiMethods.patch(`/materials/outsource-orders/${outsourceOrder?.id}/`, data);
    };

    const {
        user: { role }
    } = useContext(AuthenticationContext);

    const canSeeSummary = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);

    const assigneeCases = () => {

        const requiredStorageKeepers = new Set(outsourceOrder.required_storage_keepers || []);
        if (Array.isArray(outsourceOrder.first_storage_keeper) && outsourceOrder.first_storage_keeper.length > 0) {
            requiredStorageKeepers.add("first_storage_keeper");
        }
        if (Array.isArray(outsourceOrder.second_storage_keeper) && outsourceOrder.second_storage_keeper.length > 0) {
            requiredStorageKeepers.add("second_storage_keeper");
        }

        const storageKeeperAssignments: any[] = (outsourceOrder.status !== OUTSOURCE_ORDER_STATUS.OPEN) ?
            Array.from(requiredStorageKeepers)
            .map((keeper) => {
                if (keeper === "first_storage_keeper") {
                    return {
                        title: "Thủ kho 1",
                        fieldName: "first_storage_keeper",
                        role: "first_storage_keeper",
                    };
                } else if (keeper === "second_storage_keeper") {
                    return {
                        title: "Thủ kho 2",
                        fieldName: "second_storage_keeper",
                        role: "second_storage_keeper",
                    };
                }
                return null; // Return null for unsupported roles
            })
            .filter((assignment: any) => assignment !== null)
            : [];

        if ([
            OUTSOURCE_ORDER_STATUS.OPEN,
            OUTSOURCE_ORDER_STATUS.EXPORTING,
            OUTSOURCE_ORDER_STATUS.PARTIALLY_RETURNED,
            OUTSOURCE_ORDER_STATUS.COMPLETED,
        ].includes(outsourceOrder.status)) {
            return [
                {
                    title: "Nhân viên hậu cần",
                    fieldName: "logistic_staff",
                    role: "logistic_staff",
                },
                ...storageKeeperAssignments,
            ];
        }

        return [];
    };

    return (
        <OutsourceWorkflowShell>
            <OutsourceDetailColumns>
                <SecondColumn>
                    {outsourceOrder.source_first ? <SourceFirstEditor order={{ ...outsourceOrder, materials }} merge={update => mergeOutsourceOrder(update)} isView={isView} /> : <>
                    <MaterialList
                        materials={materials as any}
                        providerId={(outsourceOrder as any).provider_id}
                        providerName={(outsourceOrder as any).provider_name}
                        mergeOutsourceOrder={mergeOutsourceOrder}
                        isView={isView}
                    />

                    <SourceMaterialList materials={materials as any} />
                    </>}

                    <ProviderInfo
                        provider={orderProvider}
                        providerName={(outsourceOrder as any).provider_name}
                        primaryInfoTitle="Nhà gia công"
                    />
                    { canSeeSummary && (
                        <Summary data={materials} undetermined={outsourceOrder.source_first} />
                    )}
                </SecondColumn>
                <SecondColumn>
                    {!isView && (
                        <Priority productOrder={outsourceOrder as any} mergeProductOrder={mergeOutsourceOrder as any} />
                    )}

                    <Assignees
                        data={outsourceOrder}
                        updateData={updateOutsourceOrder}
                        fetchData={fetchOutsourceOrder}
                        mergeOutsourceOrder={mergeOutsourceOrder}
                        fields={[...assigneeCases()]}
                    />

                    <Note
                        text={outsourceOrder.note}
                        setText={(value: string) => mergeOutsourceOrder({ note: value })}
                        isView={isView}
                    />
                </SecondColumn>
            </OutsourceDetailColumns>
        </OutsourceWorkflowShell>
    );
};
